import React, { useEffect, useRef, useState } from 'react';

const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.js';
const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@5/dist/maplibre-gl.css';
const FALLBACK_CENTER = [-98.35, 39.5];

function loadMapLibre() {
  if (window.maplibregl) return Promise.resolve(window.maplibregl);
  return new Promise((resolve, reject) => {
    if (!document.querySelector('link[data-hc-maplibre]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = MAPLIBRE_CSS;
      link.dataset.hcMaplibre = '1';
      document.head.appendChild(link);
    }
    const existing = document.querySelector('script[data-hc-maplibre]');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.maplibregl));
      existing.addEventListener('error', () => reject(new Error('maplibre')));
      return;
    }
    const script = document.createElement('script');
    script.src = MAPLIBRE_JS;
    script.async = true;
    script.dataset.hcMaplibre = '1';
    script.onload = () => {
      if (window.maplibregl) resolve(window.maplibregl);
      else reject(new Error('maplibre'));
    };
    script.onerror = () => reject(new Error('maplibre'));
    document.head.appendChild(script);
  });
}

function hasVenue(event) {
  return event && Number.isFinite(Number(event.latitude)) && Number.isFinite(Number(event.longitude));
}

function circleRing(latitude, longitude, radiusM) {
  const steps = 64;
  const earth = 6378137;
  const lat = (latitude * Math.PI) / 180;
  const lng = (longitude * Math.PI) / 180;
  const angular = radiusM / earth;
  const ring = [];
  for (let i = 0; i <= steps; i += 1) {
    const bearing = (i / steps) * 2 * Math.PI;
    const lat2 = Math.asin(
      Math.sin(lat) * Math.cos(angular) + Math.cos(lat) * Math.sin(angular) * Math.cos(bearing)
    );
    const lng2 = lng + Math.atan2(
      Math.sin(bearing) * Math.sin(angular) * Math.cos(lat),
      Math.cos(angular) - Math.sin(lat) * Math.sin(lat2)
    );
    ring.push([(lng2 * 180) / Math.PI, (lat2 * 180) / Math.PI]);
  }
  return ring;
}

function venueNode() {
  const node = document.createElement('div');
  node.className = 'games-venue-pin';
  return node;
}

function youNode() {
  const node = document.createElement('div');
  node.className = 'games-you-pin';
  node.textContent = 'You';
  node.style.cssText = [
    'min-width:36px',
    'height:28px',
    'padding:0 8px',
    'border:2px solid #fff',
    'border-radius:14px',
    'background:#4285f4',
    'color:#fff',
    'font:600 11px/24px sans-serif',
    'text-align:center',
    'box-shadow:0 1px 4px rgba(0,0,0,0.25)',
    'box-sizing:border-box',
  ].join(';');
  return node;
}

function CheckInMap({ event, userCoord, onLocate }) {
  const hostRef = useRef(null);
  const mapRef = useRef(null);
  const youRef = useRef(null);
  const userCoordRef = useRef(userCoord);
  const framedRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);
  const [locating, setLocating] = useState(false);
  userCoordRef.current = userCoord;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return undefined;
    let cancelled = false;
    let colorObserver = null;
    let map;
    loadMapLibre()
      .then((maplibregl) => {
        if (cancelled || !hostRef.current) return;
        const venue = hasVenue(event)
          ? [Number(event.longitude), Number(event.latitude)]
          : null;
        map = new maplibregl.Map({
          container: hostRef.current,
          style: STYLE_URL,
          center: venue || FALLBACK_CENTER,
          zoom: venue ? 15 : 3,
          attributionControl: false,
        });
        mapRef.current = map;
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
        const syncYou = ({ frame } = {}) => {
          const coord = userCoordRef.current;
          if (!coord || !Number.isFinite(coord.latitude) || !Number.isFinite(coord.longitude)) return;
          const lngLat = [coord.longitude, coord.latitude];
          if (!youRef.current) {
            youRef.current = new maplibregl.Marker({ element: youNode(), anchor: 'center' })
              .setLngLat(lngLat)
              .addTo(map);
          } else {
            youRef.current.setLngLat(lngLat);
          }
          if (!frame) return;
          if (venue) {
            const bounds = new maplibregl.LngLatBounds(venue, venue);
            bounds.extend(lngLat);
            map.fitBounds(bounds, { padding: 72, maxZoom: 16, animate: false });
          } else {
            map.easeTo({ center: lngLat, zoom: 15, duration: 400 });
          }
        };
        map.__syncYou = syncYou;
        map.on('load', () => {
          if (cancelled) return;
          if (venue) {
            const radius = Number(event.check_in_radius_m) || 150;
            map.addSource('checkin-ring', {
              type: 'geojson',
              data: {
                type: 'Feature',
                geometry: { type: 'Polygon', coordinates: [circleRing(venue[1], venue[0], radius)] },
              },
            });
            const readRingColor = () =>
              getComputedStyle(document.documentElement).getPropertyValue('--hc-app-primary').trim() ||
              '#00c8ff';
            const ringColor = readRingColor();
            map.addLayer({
              id: 'checkin-ring-fill',
              type: 'fill',
              source: 'checkin-ring',
              paint: { 'fill-color': ringColor, 'fill-opacity': 0.18 },
            });
            map.addLayer({
              id: 'checkin-ring-line',
              type: 'line',
              source: 'checkin-ring',
              paint: { 'line-color': ringColor, 'line-width': 2 },
            });
            if (typeof MutationObserver !== 'undefined') {
              colorObserver = new MutationObserver(() => {
                const next = readRingColor();
                map.setPaintProperty('checkin-ring-fill', 'fill-color', next);
                map.setPaintProperty('checkin-ring-line', 'line-color', next);
              });
              colorObserver.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['style'],
              });
            }
            new maplibregl.Marker({ element: venueNode(), anchor: 'center' })
              .setLngLat(venue)
              .addTo(map);
          }
          syncYou({ frame: !framedRef.current });
          if (userCoordRef.current) framedRef.current = true;
          map.resize();
          setReady(true);
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      if (colorObserver) colorObserver.disconnect();
      setReady(false);
      framedRef.current = false;
      youRef.current = null;
      mapRef.current = null;
      if (map) map.remove();
    };
  }, [event]);

  useEffect(() => {
    if (!ready) return;
    const map = mapRef.current;
    if (!map || !map.__syncYou) return;
    const frame = !framedRef.current && !!userCoord;
    map.__syncYou({ frame });
    if (userCoord) framedRef.current = true;
  }, [ready, userCoord]);

  const focusOn = (coords) => {
    const map = mapRef.current;
    if (!map || !coords) return;
    userCoordRef.current = coords;
    framedRef.current = true;
    if (map.__syncYou) map.__syncYou({ frame: false });
    map.easeTo({
      center: [coords.longitude, coords.latitude],
      zoom: 15,
      duration: 400,
    });
  };

  const recenter = async () => {
    if (locating || !onLocate) return;
    const existing = userCoordRef.current;
    if (existing && Number.isFinite(existing.latitude) && Number.isFinite(existing.longitude)) {
      focusOn(existing);
    }
    setLocating(true);
    try {
      const coords = await onLocate();
      if (coords) focusOn(coords);
    } catch (err) {
      if (!userCoordRef.current) {
        window.alert(err?.message || 'Location is unavailable. Allow location access and try again.');
      }
    } finally {
      setLocating(false);
    }
  };

  if (failed) {
    return <p className="games-checkin-error">Map could not be loaded. Please try again later.</p>;
  }

  return (
    <div className="games-checkin-map-wrap">
      <div ref={hostRef} className="games-checkin-map" />
      <button
        type="button"
        className={`games-locate${locating ? ' is-loading' : ''}`}
        aria-label="My location"
        disabled={locating}
        onClick={recenter}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" />
          <path d="M12 3.5v2.2M12 18.3v2.2M3.5 12h2.2M18.3 12h2.2" />
          <circle cx="12" cy="12" r="7.2" />
        </svg>
      </button>
    </div>
  );
}

export default CheckInMap;
